/**
 * Helper functions for filtering API data by VPC
 */

// Interface for API response items
interface ApiItem {
  elementId: string;
  vpcId: string;
  displayLabel?: string;
  description?: string | null;
  private_ip_address?: string | null;
}

/**
 * Filter routing tables by VPC ID
 * @param fullRouteTablesData - Array of route table objects from API
 * @param selectedVpc - Selected VPC ID
 * @returns Array of filtered route table objects with value and label
 */
export const getFilteredRoutingTables = (fullRouteTablesData: ApiItem[], selectedVpc: string): Array<{ value: string; label: string }> => {
  let filtered: ApiItem[];
  if (!selectedVpc) {
    filtered = fullRouteTablesData.filter((item) => item.elementId != null && item.elementId !== '');
  } else {
    filtered = fullRouteTablesData
      .filter((item) => item.vpcId === selectedVpc && item.elementId != null && item.elementId !== '');
  }
  return filtered.map((item) => ({
    value: item.elementId,
    label: item.displayLabel || item.elementId
  }));
};

/**
 * Filter network interfaces by VPC ID
 * @param fullNetworkInterfacesData - Array of network interface objects from API
 * @param selectedVpc - Selected VPC ID
 * @returns Array of filtered network interface objects with value and label
 */
export const getFilteredNetworkInterfaces = (fullNetworkInterfacesData: ApiItem[], selectedVpc: string): Array<{ value: string; label: string }> => {
  let filtered: ApiItem[];
  if (!selectedVpc) {
    filtered = fullNetworkInterfacesData.filter((item) => item.elementId != null && item.elementId !== '');
  } else {
    filtered = fullNetworkInterfacesData
      .filter((item) => item.vpcId === selectedVpc && item.elementId != null && item.elementId !== '');
  }
  return filtered.map((item) => {
    const baseLabel = item.displayLabel || item.elementId;
    const description = item.description || '';
    const privateIpAddress = item.private_ip_address || '';
    
    // Build label with description and private_ip_address in parentheses
    let label = baseLabel;
    const additionalInfo: string[] = [];
    
    if (description) {
      additionalInfo.push(description);
    }
    if (privateIpAddress) {
      additionalInfo.push(privateIpAddress);
    }
    
    if (additionalInfo.length > 0) {
      label = `${baseLabel} (${additionalInfo.join(', ')})`;
    }
    
    return {
      value: item.elementId,
      label: label
    };
  });
};

/**
 * Get empty items message for dropdowns based on VPC selection and loading state
 * @param selectedVpc - Selected VPC ID
 * @param isLoading - Loading state
 * @param defaultMessage - Default message when VPC is selected and not loading
 * @returns Appropriate message for dropdown
 */
export const getEmptyItemsMessage = (
  selectedVpc: string, 
  isLoading: boolean, 
  defaultMessage = "No items available"
): string => {
  if (!selectedVpc) {
    return "Please select a VPC first";
  }
  if (isLoading) {
    return "Loading...";
  }
  return defaultMessage;
};
